import { type ReactNode, useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

export type Tab = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
};

export type TabsProps = {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  className?: string;
  /** When true, tabs shrink to fit their content instead of stretching equally. */
  fitted?: boolean;
};

export function Tabs({
  tabs,
  defaultTab,
  activeTab: controlledTab,
  onChange,
  className = "",
  fitted = false,
}: TabsProps) {
  const [internalTab, setInternalTab] = useState(defaultTab || tabs[0]?.id);
  const activeTab = controlledTab ?? internalTab;
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTabClick = (tabId: string) => {
    setInternalTab(tabId);
    onChange?.(tabId);
  };

  useEffect(() => {
    const activeButton = tabRefs.current.get(activeTab);
    const container = containerRef.current;
    if (activeButton && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setSliderStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      });
    }
  }, [activeTab]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex p-1 border border-surface-700 rounded-lg bg-surface-900",
        fitted && "justify-center",
        className,
      )}
    >
      <div
        className="absolute top-1 bottom-1 bg-brand-500/10 rounded-md transition-all duration-300 ease-out"
        style={{ left: sliderStyle.left, width: sliderStyle.width }}
      />
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.id, el);
            }}
            type="button"
            onClick={() => handleTabClick(tab.id)}
            className={cn(
              "relative flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold rounded-md transition-colors",
              !fitted && "flex-1",
              isActive ? "text-brand-500" : "text-surface-400 hover:text-surface-200",
            )}
          >
            {tab.icon && <span className="[&_svg]:w-4 [&_svg]:h-4">{tab.icon}</span>}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
